import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to fetch site content from the Mini CMS
 * Falls back to provided default if content not found or not visible
 * 
 * @param {string} key - The content key to fetch
 * @param {string} defaultValue - Fallback value if content not found
 * @returns {string} The content value
 */
export const useSiteContent = (key, defaultValue) => {
  const [value, setValue] = useState(defaultValue);
  
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/site-content/public/${key}`);
        if (response.ok) {
          const data = await response.json();
          if (data.visible && data.value) {
            setValue(data.value);
          }
        }
      } catch (error) {
        // Silently fail and use default
        console.debug(`Using default for ${key}`);
      }
    };
    
    fetchContent();
  }, [key, defaultValue]);
  
  return value;
};

/**
 * Hook to fetch all content for a section
 * Returns a map of key -> value
 * 
 * @param {string} section - The section to fetch
 * @param {Object} defaults - Default values keyed by content key
 * @returns {Object} Map of key -> value
 */
export const useSectionContent = (section, defaults = {}) => {
  const [content, setContent] = useState(defaults);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/site-content/public/section/${section}`);
        if (response.ok) {
          const data = await response.json();
          const contentMap = { ...defaults };
          
          (data.items || []).forEach(item => {
            if (item.visible && item.value) {
              contentMap[item.key] = item.value;
            }
          });
          
          setContent(contentMap);
        }
      } catch (error) {
        // Silently fail and use defaults
        console.debug(`Using defaults for section ${section}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchContent();
  }, [section]);
  
  return { content, loading };
};

/**
 * Hook to fetch FAQ items from CMS
 * Returns array of { question, answer } objects
 * 
 * @param {Array} defaultFaqs - Default FAQ items
 * @returns {Array} FAQ items
 */
export const useFaqContent = (defaultFaqs = []) => {
  const [faqs, setFaqs] = useState(defaultFaqs);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/site-content/public/section/faq`);
        if (response.ok) {
          const data = await response.json();
          const items = data.items || [];
          
          // Build FAQ pairs from q1/a1, q2/a2, etc.
          const faqMap = {};
          items.forEach(item => {
            if (item.visible && item.value) {
              faqMap[item.key] = item.value;
            }
          });
          
          // Extract FAQ pairs
          const newFaqs = [];
          for (let i = 1; i <= 10; i++) {
            const q = faqMap[`faq_q${i}`];
            const a = faqMap[`faq_a${i}`];
            if (q && a) {
              newFaqs.push({ question: q, answer: a });
            }
          }
          
          if (newFaqs.length > 0) {
            setFaqs(newFaqs);
          }
        }
      } catch (error) {
        console.debug('Using default FAQs');
      } finally {
        setLoading(false);
      }
    };
    
    fetchContent();
  }, []);
  
  return { faqs, loading };
};

export default useSiteContent;
